import React from 'react'
import { Route, Redirect } from 'react-router-dom'

import {
  ErrorBoundary,
  SvgIcon,
  Heading
} from '../src/components'

import {
  hasResponsibility
} from '../src/utils'

const auth = {
  login(username, pass, cb, auth) {
    if (localStorage.token) {
      if (cb) cb(true)
      return
    }

    if (auth === "default") {
      this.getToken(username, pass, (res) => {
        if (res.authenticated) {
          localStorage.token = res.token
          localStorage.auth = 'default'
          if (cb) cb(true)
        }
        else {
          if (cb) cb(false, res.error)
        }
      })
    }

    if (auth === "worklight") {
      // TODO: create getWorkLightToken function
      fetch(`${process.env.REACT_APP_WORKLIGHT_API_URL}/auth/getauthtoken`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          "appClientId": process.env.REACT_APP_WORKLIGHT_APP_CLIENT_ID,
          "productId": process.env.REACT_APP_WORKLIGHT_MESSAGEPLUS_PRODUCT_ID,
          "username": username,
          "password": pass
        })
      })
      .then((res) => {
        res.json()
        .then(res => {
          localStorage.token = res.idToken
          localStorage.auth = 'worklight'
          if (cb) {
            cb(true)
          }
        })
        .catch((error) => {
          if (cb) {
            cb(false, error)
          }
        })
      })
      .catch((error) => {
        if (cb) {
          cb(false, error)
        }
      })
    }
  },

  logout(location) {
    localStorage.clear()
    window.location.replace("/login")  // when React version updated, we can use useHistory hook instead should we want to
  },

  loggedIn() {
    return !!localStorage.token
  },

  requestResetPassword(email, cb) {
    let body = new FormData()
    body.append('email', email)

    fetch(`${process.env.REACT_APP_API_ROOT}/rest-auth/password/reset/`, {
      method: 'post',
      body: body
    })
    .then((data) => {
      if (data.ok) {  // covers 200-range of http responses
        console.log("Password reset requested!")
      } else {
        console.log(`Password reset request failed.  HTTP: ${data.status} - ${data.statusText}`)
      }
      return data.json()
    })
    .then((body) => {
      if (body.detail) {
        cb({
          requested: true,
          detail: body.detail
        })
      } else {
        cb({
          requested: false,
          error: body
        })
      }
    })
    .catch((error) => {
      console.log('Password reset request failed', error)
      cb({
        requested: false,
        error: error
      })
    })
  },

  resetPassword(uid, token, newPassword, cb) {
    let body = new FormData()
    body.append('uid', uid)
    body.append('token', token)
    body.append('new_password1', newPassword)
    body.append('new_password2', newPassword)

    fetch(`${process.env.REACT_APP_API_ROOT}/rest-auth/password/reset/confirm/`, {
      method: 'post',
      body: body
    })
    .then((data) => {
      if (data.ok) {  // covers 200-range of http responses
        console.log("Password reset!")
      } else {
        console.log(`Password reset failed.  HTTP: ${data.status} - ${data.statusText}`)
      }
      return data.json()
    })
    .then((body) => {
      if (body.detail) {
        cb({
          reset: true,
          detail: body.detail
        })
      } else {
        cb({
          reset: false,
          error: body
        })
      }
    })
    .catch((error) => {
      console.log('Password reset failed', error)
    })
  },

  getToken(email, password, cb) {
    let body = new FormData()
    body.append('email', email)
    body.append('password', password)

    fetch(`${process.env.REACT_APP_API_ROOT}/rest-auth/login/`, {
      method: 'post',
      body: body
    })
    .then((data) => {
      if (data.ok) {  // covers 200-range of http responses
        console.log("Login successful!")
      } else {
        console.log(`Auth failed.  HTTP: ${data.status} - ${data.statusText}`)
      }
      return data.json()
    })
    .then((body) => {
      if (body.key) {
        cb({
          authenticated: true,
          token: body.key
        })
      } else {
        cb({
          authenticated: false,
          error: body
        })
      }
    })
    .catch((error) => {
      console.log('Login failed', error)
      cb({
        authenticated: false,
        error: error
      })
    })
  },
}

export const PrivateRoute = ({ component: Component, render, ...rest }) => (
  <Route {...rest} render={props => (
    auth.loggedIn()
      ? Component ? <Component {...props}/> : render()
      : (
          <Redirect to={{
            pathname: '/login',
            state: { from: props.location }
          }}/>
        )
  )}/>
)

export const PrivateRouteInternal = ({ component: Component, render, ...rest }) => (
  <Route {...rest} render={props => (
    auth.loggedIn()
      ? JSON.parse(localStorage.getItem("isUserPicsaEmployee"))
        ? Component ? <Component {...props} /> : render(props)
        : <div className="slds-m-around_xx-large" style={{ fontSize: "larger" }}>
            <SvgIcon
              variant="utility"
              symbol="lock"
              colour="error"
              className="slds-m-right_medium"
            />
            You are not a Picsa employee and therefore don&rsquo;t have access rights to this page.
          </div>
      : (
          <Redirect to={{
            pathname: '/login',
            state: { from: props.location }
          }}/>
        )
  )}/>
)

export const PrivateRouteInternalWithErrorBoundary = ({ component: Component, errorBoundaryText, ...rest }) => (
  <Route {...rest} render={props => (
    auth.loggedIn()
      ? JSON.parse(localStorage.getItem("isUserPicsaEmployee"))
        ? <ErrorBoundary
            errorText={errorBoundaryText || "Something went wrong loading the page."}
            fillOutErrorReport={true}
            className="slds-p-around_large">
            <Component {...props}/>
          </ErrorBoundary>
        : <div className="slds-m-around_xx-large" style={{ fontSize: "larger" }}>
            <SvgIcon
              variant="utility"
              symbol="lock"
              colour="error"
              className="slds-m-right_medium"
            />
            You are not a Picsa employee and therefore don&rsquo;t have access rights to this page.
          </div>
      : (
          <Redirect to={{
            pathname: '/login',
            state: { from: props.location }
          }}/>
        )
  )}/>
)

export const PrivateRouteWithErrorBoundary = ({ component: Component, errorBoundaryText, ...rest }) => (
  <Route {...rest} render={props => (
    auth.loggedIn()
    ? (
        <ErrorBoundary
          errorText={errorBoundaryText || "Something went wrong loading the page."}
          className="slds-p-around_large">
          <Component {...props}/>
        </ErrorBoundary>
      )
    : (
        <Redirect to={{
          pathname: '/login',
          state: { from: props.location }
        }}/>
      )
  )}/>
)

export const PrivateRouteWithPermission = ({ component: Component, responsibility, model, instance,  ...rest }) => (
  <Route {...rest} render={props => (
    auth.loggedIn()
    ? hasResponsibility("self", responsibility, model, instance)
      ? <Component {...props} />
      : <div className="slds-p-around_xx-large slds-grid">
          <SvgIcon
            variant="utility"
            symbol="lock"
            className="slds-m-right_medium"
          />
          <Heading className="slds-m-top_x-small">
            Unfortunately, you don&rsquo;t have permission to view this page.
          </Heading>
        </div>
    : (
        <Redirect to={{
          pathname: '/login',
          state: { from: props.location }
        }}/>
      )
  )}/>
)

export default auth



// WEBPACK FOOTER //
// ./src/auth.js